import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { gql } from 'graphql-tag';
import { GraphqlClientService } from '@core/services/graphql-client.service';
import { API } from '@configs/api.config';
import { OperationLabel, OperationLabelSyncRequest } from '@core/models/operation-label.model';
import { JobFileContainer } from '@core/models/job-file/job-file-container.model';

const findInvoicedLoadIds = gql`
  query findInvoicedLoadIds($loadIds: [String!]!) {
    findInvoicedLoadIds(loadIds: $loadIds)
  }`;

/** Operation labels (ms-org-manager) and their assignment to loads (ms-jobfile). PB-5281. */
@Injectable({
  providedIn: 'root'
})
export class OperationLabelService {

  constructor(private http: HttpClient,
              private gqlService: GraphqlClientService) {}

  /** Labels of one operation; never includes deleted labels. */
  getLabels(operationId: string, includeInactive = true, withUsage = false): Observable<OperationLabel[]> {
    const params = new HttpParams()
      .set('includeInactive', String(includeInactive))
      .set('withUsage', String(withUsage));
    return this.http.get<OperationLabel[]>(API.organization.getOperationLabels.format([operationId]), {params});
  }

  /** Saves the staged label changes of one operation and returns its labels. */
  syncLabels(operationId: string, request: OperationLabelSyncRequest): Observable<OperationLabel[]> {
    return this.http.post<OperationLabel[]>(API.organization.syncOperationLabels.format([operationId]), request);
  }

  /** Labels of several operations (all accessible ones when none given) for list filters. */
  getLabelsForOperations(operationIds: string[], includeDeleted = true): Observable<OperationLabel[]> {
    let params = new HttpParams().set('includeDeleted', String(includeDeleted));
    (operationIds || []).filter(id => !!id).forEach(id => params = params.append('operationIds', id));
    return this.http.get<OperationLabel[]>(API.organization.getLabelsForOperations, {params});
  }

  updateContainerLabels(orgId: string, jobRefId: string, containerId: string, labelIds: string[]): Observable<JobFileContainer> {
    const url = API.jobFile.updateContainerLabels.format([orgId, jobRefId, containerId]);
    return this.http.put<JobFileContainer>(url, {labelIds});
  }

  /** The loads that are on an invoice; their labels are read-only (ms-invoice). */
  getInvoicedLoadIds(loadIds: string[]): Observable<string[]> {
    return this.gqlService.queryDataFromGQL<string[]>(
      {loadIds}, findInvoicedLoadIds, 'findInvoicedLoadIds', 'invoice-endpoint');
  }
}
